import { useEffect, useMemo, useRef, useState } from "react"
import { Loader2, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { useAllAuthors } from "@/lib/queries/authors"

/**
 * CreatorPicker — autocomplete-style input for the AUTHOR field on
 * catalog forms (chapter / album / audioQuote). Suggests names that
 * already appear on catalog docs so admins stop introducing case-
 * variant duplicates ("derek prince" vs "Derek Prince").
 *
 * V-Q9 (2026-08-17): switched source from the coach directory to a
 * distinct-authors aggregation over chapter.author + album.author +
 * video.narrated_by + video.author (POST /admin/author_list).
 * Previous behavior mixed coach names into the author dropdown,
 * which broke the FE-locked distinction that catalog content has
 * "authors" while messages have "coaches" — the two shouldn't
 * overlap in the pickers.
 *
 * Coach picker is now separate — MessageCompose uses useAllCoaches
 * directly. Nothing else in the app uses CreatorPicker.
 *
 * The form value is the AUTHOR NAME STRING (not an id) — matches the
 * legacy `narrated_by` / `author` schema fields. There is no Author
 * collection to persist to; typing a new name and saving the parent
 * form IS the creation flow, and the new name surfaces in future
 * /admin/author_list responses via the aggregation.
 *
 * Usage with react-hook-form (unchanged):
 *
 *   <Controller
 *     name="narrater"
 *     control={control}
 *     render={({ field }) => (
 *       <CreatorPicker
 *         value={field.value || ""}
 *         onChange={field.onChange}
 *         placeholder="Pick or type an author"
 *       />
 *     )}
 *   />
 *
 * UX rules:
 *   - Free typing is allowed (unchanged). The field still submits
 *     whatever string the admin typed.
 *   - The dropdown shows up to 8 fuzzy matches sorted by similarity.
 *   - No "Create new" action — authors are implicit on save. Removed
 *     the previous inline coach-create so admins can't accidentally
 *     mint a fake Coach record when they meant to name an author.
 */

export type CreatorPickerProps = {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  /** Deprecated — kept for prop-shape back-compat; there is no
   *  inline-create action to disable anymore (V-Q9). */
  disableInlineCreate?: boolean
  /** Deprecated — no create action fires, so no callback fires. */
  onCreated?: (created: { id: string; name: string }) => void
  /** id attribute for label association in forms. */
  id?: string
  /** Extra Tailwind classes for the input wrapper. */
  className?: string
}

export function CreatorPicker({
  value,
  onChange,
  placeholder = "Pick or type an author",
  id,
  className,
}: CreatorPickerProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const allQ = useAllAuthors()

  // Close dropdown on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [])

  // Filter + sort suggestions
  const trimmed = (value || "").trim()
  const suggestions = useMemo(() => {
    const items = allQ.data || []
    if (!trimmed) return items.slice(0, 8)
    const lower = trimmed.toLowerCase()
    // Score: exact > prefix > contains. Cap to 8.
    const scored = items.map((c) => {
      const lname = c.name.toLowerCase()
      let score = 0
      if (lname === lower) score = 3
      else if (lname.startsWith(lower)) score = 2
      else if (lname.includes(lower)) score = 1
      return { ...c, score }
    })
    return scored
      .filter((c) => c.score > 0)
      .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
      .slice(0, 8)
  }, [allQ.data, trimmed])

  // V-Q9: no inline-create action anymore. Author names are free-text
  // and get persisted when the parent form saves. See the header
  // comment for background.

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <Input
        id={id}
        ref={inputRef}
        type="text"
        autoComplete="off"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
      />

      {open && (allQ.isLoading || suggestions.length > 0 || (!allQ.isLoading && trimmed)) && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
          {allQ.isLoading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading authors…
            </div>
          )}

          {suggestions.length > 0 && (
            <ul className="max-h-64 overflow-y-auto py-1">
              {suggestions.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      // Use mousedown so it fires before the input
                      // blur closes the dropdown.
                      e.preventDefault()
                      onChange(c.name)
                      setOpen(false)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-muted text-[10px] font-medium uppercase text-muted-foreground">
                      {(c.name[0] || "?").toUpperCase()}
                    </span>
                    <span className="truncate">{c.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!allQ.isLoading && suggestions.length === 0 && trimmed && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              No matching authors — press save to add "{trimmed}" as a new author.
            </div>
          )}
        </div>
      )}

      {value && !open && (
        <button
          type="button"
          aria-label="Clear creator"
          onClick={() => {
            onChange("")
            inputRef.current?.focus()
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}
